// src/views/EOrderLabelModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import './EOrderLabelModal.css';
import {
  FaTimes, FaPlus, FaMinus, FaBoxes, FaTruck, FaShippingFast, FaPrint, FaExclamationTriangle,
} from 'react-icons/fa';
import { SiUps, SiFedex } from 'react-icons/si';
const API = import.meta.env.VITE_API_URL;
/* ==== provider helpers (icons + friendly names) ==== */
const isUPS = (t) => String(t || '').toLowerCase().includes('ups');
const isFedEx = (t) => String(t || '').toLowerCase().includes('fedex');
const isShipEngine = (t) => String(t || '').toLowerCase().includes('shipengine');
const UPS_NAME_MAP = {
  '01': 'UPS Next Day Air®', '02': 'UPS 2nd Day Air®', '03': 'UPS Ground', '07': 'UPS Worldwide Express®',
  '08': 'UPS Worldwide Expedited®', '11': 'UPS Standard', '12': 'UPS 3 Day Select®', '13': 'UPS Next Day Air Saver®',
  '14': 'UPS Next Day Air® Early', '54': 'UPS Worldwide Express Freight®', '59': 'UPS 2nd Day Air® A.M.', '65': 'UPS Worldwide Saver®', '70': 'UPS Access Point™ Economy'
};
const getUpsFriendly = (c, fb) => UPS_NAME_MAP[String(c || '').toUpperCase()] || fb || c || '';
const getUpsIcon = (c) => {
  const k = String(c || '').toUpperCase();
  if (['01', '13', '14'].includes(k)) return <FaShippingFast className="svc-icon ups" title="UPS Air" />;
  if (['02', '12', '59'].includes(k)) return <FaShippingFast className="svc-icon ups" title="UPS 2/3 Day" />;
  if (['03'].includes(k)) return <FaTruck className="svc-icon ups" title="UPS Ground" />;
  return <SiUps className="svc-icon ups" title="UPS" />;
};
const FEDEX_NAME_MAP = {
  FEDEX_GROUND: 'FedEx Ground', GROUND_HOME_DELIVERY: 'FedEx Home Delivery', FEDEX_HOME_DELIVERY: 'FedEx Home Delivery',
  FEDEX_2_DAY: 'FedEx 2Day®', FEDEX_2_DAY_AM: 'FedEx 2Day® A.M.', FEDEX_EXPRESS_SAVER: 'FedEx Express Saver®',
  PRIORITY_OVERNIGHT: 'FedEx Priority Overnight®', STANDARD_OVERNIGHT: 'FedEx Standard Overnight®',
  FIRST_OVERNIGHT: 'FedEx First Overnight®', INTERNATIONAL_PRIORITY: 'FedEx International Priority®',
  INTERNATIONAL_ECONOMY: 'FedEx International Economy®'
};
const getFedexFriendly = (t, fb) => FEDEX_NAME_MAP[String(t || '').toUpperCase()] || fb || t || '';
const getFedexIcon = (t) =>
  String(t || '').toUpperCase().includes('OVERNIGHT')
    ? <FaShippingFast className="svc-icon fedex" title="FedEx Overnight" />
    : <FaTruck className="svc-icon fedex" title="FedEx Ground" />;
const fmtCurrency = (n, c = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency: c }).format(Number(n || 0));
const sanitizePhone = (p) => { const d = String(p || '').replace(/\D/g, ''); return d.length >= 10 ? d.slice(0, 15) : '0000000000'; };
function friendlyPackageType(pt) {
  if (!pt) return '';
  const raw = String(pt).trim();
  const lower = raw.toLowerCase();
  const upper = raw.toUpperCase();
  // Lower-case mappings (ShipEngine package_type and our internal codes)
  const mapLower = {
    // Generic
    'package': 'Your Packaging',
    'package_thick_envelope': 'Thick Envelope',
    'large_envelope_or_flat': 'Large Envelope / Flat',
    'letter': 'Letter',
    'parcel': 'Parcel',
    // USPS flat-rate (lower-case style from package_type)
    'flat_rate_envelope': 'Flat Rate Envelope',
    'legal_flat_rate_envelope': 'Legal Flat Rate Envelope',
    'padded_flat_rate_envelope': 'Padded Flat Rate Envelope',
    'small_flat_rate_box': 'Small Flat Rate Box',
    'medium_flat_rate_box': 'Medium Flat Rate Box',
    'large_flat_rate_box': 'Large Flat Rate Box',
    'regional_rate_box_a': 'Regional Rate Box A',
    'regional_rate_box_b': 'Regional Rate Box B',
    'flat_rate_box': 'Flat Rate Box',
    // FedEx flat-rate (lower-case style)
    'fedex_small_box': 'FedEx Small Box',
    'fedex_medium_box': 'FedEx Medium Box',
    'fedex_large_box': 'FedEx Large Box',
    'fedex_envelope': 'FedEx Envelope',
    'fedex_pak': 'FedEx Pak',
  };
  // Upper-case mappings (ShipEngine package_code style)
  const mapUpper = {
    // USPS (common ShipEngine codes)
    'USPS_FLAT_SM': 'Small Flat Rate Box',
    'USPS_FLAT_MD': 'Medium Flat Rate Box',
    'USPS_FLAT_LG': 'Large Flat Rate Box',
    'USPS_FLAT_ENV': 'Flat Rate Envelope',
    'USPS_FLAT_LEGAL_ENV': 'Legal Flat Rate Envelope',
    'USPS_FLAT_PADDED_ENV': 'Padded Flat Rate Envelope',
    'USPS_REGIONAL_A': 'Regional Rate Box A',
    'USPS_REGIONAL_B': 'Regional Rate Box B',
    'USPS_LG_ENV': 'Large Envelope / Flat',
    'USPS_LETTER': 'Letter',
    // FedEx (common ShipEngine codes)
    'FEDEX_SMALL_BOX': 'FedEx Small Box',
    'FEDEX_MEDIUM_BOX': 'FedEx Medium Box',
    'FEDEX_LARGE_BOX': 'FedEx Large Box',
    'FEDEX_ENVELOPE': 'FedEx Envelope',
    'FEDEX_PAK': 'FedEx Pak',
  };
  console.log('friendlyPackageType input:', pt); // Debug the input
  return mapLower[lower] || mapUpper[upper] || raw; // Prioritize lower-case package_type
}
// Build a summary string of the packages selected in the UI (fallback when the rate response
// doesn't echo back a package_code per rate). Example: "Box: Medium Flat Rate Box ×2, Small Flat Rate Box"
function summarizePackagesForDisplay(pkgList = []) {
  if (!Array.isArray(pkgList) || pkgList.length === 0) return '';
  const counts = new Map();
  for (const p of pkgList) {
    const code = (p?.packageCode || '').trim();
    const key = code || 'package';
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  // Build friendly labels
  const parts = [];
  for (const [key, count] of counts.entries()) {
    const label = key === 'package' ? 'Your Packaging' : (friendlyPackageType(key) || key);
    parts.push(count > 1 ? `${label} ×${count}` : `${label}`);
  }
  if (!parts.length) return '';
  return `Box: ${parts.join(', ')}`;
}
function ServiceCell({ providerServiceType, serviceCode, serviceName }) {
  return isUPS(providerServiceType)
    ? <>{getUpsIcon(serviceCode)} {getUpsFriendly(serviceCode, serviceName)}</>
    : isFedEx(providerServiceType)
      ? <>{getFedexIcon(serviceName)} {getFedexFriendly(serviceName, serviceName)}</>
      : <>{serviceName || serviceCode || 'Service'}</>;
}
export default function EOrderLabelModal({ eorderId, clientName, orderNumber, onClose }) {
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [header, setHeader] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  // app config (ship-from)
  const [appCfg, setAppCfg] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(true);
  // overrides & packages
  const [weightOverrides, setWeightOverrides] = useState({}); // { [rowId]: lbs }
  /**
   * bundlePackages item shape:
   * {
   * id, weight, unit, length, width, height, dimUnit, selectedShipPkgId, items:[{id, qty}]
   * }
   */
  const [bundlePackages, setBundlePackages] = useState([
    { id: 1, weight: '', unit: 'LBS', length: '', width: '', height: '', dimUnit: 'IN', selectedShipPkgId: '', items: [] }
  ]);
  const [bundleSelections, setBundleSelections] = useState({});
  // Shipping package options (client + global)
  const [shipPkgOptions, setShipPkgOptions] = useState([]); // {id, clientid, name, code, length,width,height,dim_unit, volume_ft}
  // Delivery options (NEW)
  const [homeDelivery, setHomeDelivery] = useState(false); // residential toggle (default to commercial)
  const [signatureRequired, setSignatureRequired] = useState(false);
  // Rates / purchase
  const [rates, setRates] = useState([]);
  const [fetchingRates, setFetchingRates] = useState(false);
  const [ratesError, setRatesError] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [shipDate, setShipDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Operator overrides for Ship-To contact (useful when Shopify order lacks phone/email)
  const [shipToPhoneOverride, setShipToPhoneOverride] = useState('');
  const [shipToEmailOverride, setShipToEmailOverride] = useState('');
  // Package limits (per carrier)
  const UPS_MAX_LBS = 150;
  // --- Helpers (OSC & weight) ---
  function flagOwnContainer(row) {
    const v = row.own_shipping_container ?? row.itemownshippingcontainer ?? row.owncontainer ?? 0;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    const s = String(v).toLowerCase(); return s === '1' || s === 'true' || s === 'y' || s === 'yes';
  }
  function rowWeightLbsRaw(row) {
    const lbs = Number(row.weight_lbs || row.itemweight || 0);
    if (lbs > 0) return lbs;
    const oz = Number(row.weight_oz || 0);
    return oz > 0 ? +(oz / 16).toFixed(2) : 0;
  }
  function rowWeightLbs(row) {
    const override = Number(weightOverrides[row.id] || 0);
    return override > 0 ? override : rowWeightLbsRaw(row);
  }
  function rowDims(row) {
    const L = Number(row.length ?? 0);
    const W = Number(row.width ?? 0);
    const H = Number(row.height ?? 0);
    if (L > 0 && W > 0 && H > 0) return `${L}×${W}×${H} in`;
    return '—';
  }
  // item volume (ft^3) from inches, use only length/width/height
  function itemVolumeFt(row) {
    const L = Number(row.length ?? 0);
    const W = Number(row.width ?? 0);
    const H = Number(row.height ?? 0);
    if (L > 0 && W > 0 && H > 0) return (L * W * H) / 1728;
    return 0;
  }
  // --- Helper: get item dims as [L,W,H] array
  function itemDimsIn(row) {
    return [Number(row.length || 0), Number(row.width || 0), Number(row.height || 0)];
  }
  // --- Helper: check if required dims fit in box dims (allowing rotation, with a factor headroom)
  function fitsDims(required, boxDims, factor = 1.05) {
    // required and boxDims are [L,W,H]; allow rotation by sorting ascending
    const req = required.map(n => Number(n || 0) * factor).sort((a,b)=>a-b);
    const box = boxDims.map(n => Number(n || 0)).sort((a,b)=>a-b);
    return req[0] <= box[0] && req[1] <= box[1] && req[2] <= box[2];
  }
  // Lookups
  const itemById = useMemo(() => {
    const m = new Map();
    for (const r of items || []) m.set(r.id, r);
    return m;
  }, [items]);
  const ownContainerItems = useMemo(() => items.filter(row => flagOwnContainer(row)), [items]);
  const bundledCandidateItems = useMemo(() => items.filter(row => !flagOwnContainer(row)), [items]);
  const ownMissingWeights = useMemo(
    () => ownContainerItems.filter(r => (rowWeightLbs(r) || 0) <= 0),
    [ownContainerItems, weightOverrides]
  );
  // Pre-computed readiness flags for enabling Get Rates
  const missingBundleWeights = useMemo(
    () => bundlePackages.some(b => (b.items || []).length > 0 && !(Number(b.weight) > 0)),
    [bundlePackages]
  );
  const missingBundleDims = useMemo(
    () => bundlePackages.some(b => {
      const hasItems = (b.items || []).length > 0;
      const L = Number(b.length), W = Number(b.width), H = Number(b.height);
      return hasItems && (!(L > 0) || !(W > 0) || !(H > 0));
    }),
    [bundlePackages]
  );
  // ===== Providers (only those assigned to this client) + Config =====
  useEffect(() => {
    let alive = true;
    const mapRouteBase = (stype) => {
      const s = String(stype || '').toLowerCase();
      if (s.includes('ups')) return '/api/shipping/ups';
      if (s.includes('fedex')) return '/api/shipping/fedex';
      if (s.includes('ehub')) return '/api/shipping/ehub';
      if (s.includes('shipengine')) return '/api/shipping/shipengine';
      if (s.includes('stamps')) return '/api/shipping/stamps';
      return '';
    };
    (async () => {
      setLoadingProviders(true);
      try {
        if (header?.clientid) {
          const r = await fetch(`${API}/api/shippingintegrations/client-profiles/${header.clientid}`);
          const j = await r.json().catch(() => ({}));
          const rows = Array.isArray(j?.data) ? j.data : [];
          const list = rows
            .filter(sp => (sp?.enabled ? Number(sp.enabled) !== 0 : true) && Number(sp?.deleted || 0) === 0)
            .map(sp => ({
              id: sp.shipping_integration_id,
              displayname: sp.displayname || `#${sp.shipping_integration_id}`,
              servicetype: sp.servicetype || '',
              routeBase: mapRouteBase(sp.servicetype),
              vendormarkup: Number(sp.vendormarkup || 0),
              juicymarkup: Number(sp.juicymarkup || 0),
              threeplmarkup: Number(sp['3plmarkup'] || 0),
              icon: sp.icon || null
            }))
            .filter(p => !!p.routeBase);
          if (alive) {
            setProviders(list);
            if (list.length === 1) {
              // Auto-select the only available profile
              setProviderId(String(list[0].id));
            }
          }
        } else {
          // No client context → show none
          if (alive) setProviders([]);
        }
      } catch {
        if (alive) setProviders([]);
      } finally {
        if (alive) setLoadingProviders(false);
      }
    })();
    return () => { alive = false; };
  }, [header?.clientid]);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingCfg(true);
      try {
        const r = await fetch(`${API}/api/appconfig`);

        const j = await r.json();
//console.log(j);

        if (!alive) return;
        setAppCfg(j || {});
      } catch {
        if (alive) setAppCfg({});
      } finally {
        if (alive) setLoadingCfg(false);
      }
    })();
    return () => { alive = false; };
  }, []);
  // ===== Header + Items =====
  useEffect(() => {
    if (!eorderId) return;
    let alive = true;
    (async () => {
      setLoadingItems(true);
      try {
        const [hRes, iRes] = await Promise.all([
          fetch(`${API}/api/eorders/${eorderId}`),
          fetch(`${API}/api/eorders/${eorderId}/items`)
        ]);
        const hdr = await hRes.json();
        const rows = await iRes.json();
        if (!alive) return;
        setHeader(hdr || {});
        // Seed overrides for Ship-To phone/email if present in header
        setShipToPhoneOverride((hdr?.shippingphone || '').trim());
        setShipToEmailOverride((hdr?.shippingemail || '').trim());
        setItems(Array.isArray(rows) ? rows : []);
      } catch {
        if (alive) { setHeader(null); setItems([]); }
      } finally {
        if (alive) setLoadingItems(false);
      }
    })();
    return () => { alive = false; };
  }, [eorderId]);
  // Fetch shipping package options (client + global) — skip for client users
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Block client users from calling internal-only shipping-packages endpoints
        try {
          const u = JSON.parse(localStorage.getItem('user') || '{}');
          const isClient = (u?.isclient === true) || (String(u?.isclient).toLowerCase() === 'true') || (String(u?.isclient) === '1') || (Number(u?.isclient) === 1);
          if (isClient) { if (alive) setShipPkgOptions([]); return; }
        } catch {}
        const list = [];
        let d1 = [];
        if (header?.clientid) {
          const r1 = await fetch(`${API}/api/shipping-packages?clientid=${encodeURIComponent(header.clientid)}&active=1`);
          const j1 = await r1.json().catch(() => ({}));
          d1 = Array.isArray(j1?.data) ? j1.data : [];
          list.push(...d1);
        }
        const r2 = await fetch(`${API}/api/shipping-packages?active=1`);
        const j2 = await r2.json().catch(() => ({}));
        const d2 = (Array.isArray(j2?.data) ? j2.data : []).filter(x => x.clientid == null);
        const d1Dedup = (arr) =>
          arr.map(r => ({
            id: r.id, clientid: r.clientid ?? null,
            name: r.name, code: r.code,
            length: Number(r.length || 0),
            width: Number(r.width || 0),
            height: Number(r.height || 0),
            dim_unit: (r.dim_unit || 'IN').toUpperCase(),
            volume_ft: Number(r.volume_ft ?? (r.length && r.width && r.height ? (r.dim_unit === 'CM'
              ? ((r.length / 2.54) * (r.width / 2.54) * (r.height / 2.54)) / 1728
              : (r.length * r.width * r.height) / 1728) : 0))
          })).sort((a, b) => {
            const ac = a.clientid == null ? 1 : 0; const bc = b.clientid == null ? 1 : 0;
            if (ac !== bc) return ac - bc;
            return a.volume_ft - b.volume_ft;
          });
        const merged = [
          ...d1Dedup(list),
          ...d1Dedup(d2.filter(g => !list.some(c => c.id === g.id)))
        ];
        if (alive) setShipPkgOptions(merged);
      } catch {
        if (alive) setShipPkgOptions([]);
      }
    })();
    return () => { alive = false; };
  }, [header?.clientid]);

  // Clear rates when provider changes
  useEffect(() => {
    setRates([]);
    setRatesError('');
  }, [providerId]);

  // ===== Ship-from / Ship-to =====
  const shipAddrs = useMemo(() => {
    const h = header || {};
    const cfg = appCfg || {};
    const shipperName = (h.clientname || clientName || 'Warehouse').toString().trim();
    const fromAddress1 = [cfg.companystreet, cfg.companysuite].filter(Boolean).join(' ').trim();
    const shipperCity = cfg.companycity || '';
    const shipperState = cfg.companystateabbrev || cfg.companystate || '';
    const shipperPostal = cfg.companyzip || '';
    const shipperCountry = cfg.companycountry || 'US';

    const shipper = {
      name: shipperName,
      accountNumber: '',
      phone: sanitizePhone(cfg.companyphone || ''),
      address: {
        address1: fromAddress1 || '',
        address2: undefined,
        city: shipperCity,
        state: shipperState,
        postalCode: shipperPostal,
        countryCode: shipperCountry || 'US',
        residential: false,
      },
    };
    const shipTo = {
      name: [h.shippingcompany, `${h.shippingfirstname || ''} ${h.shippinglastname || ''}`]
        .map(s => (s || '').trim())
        .filter(Boolean)
        .join(' • ') || 'Recipient',
      address1: h.shippingaddress1 || '',
      address2: h.shippingaddress2 || '',
      city: h.shippingcity || '',
      state: h.shippingstate || '',
      postalCode: h.shippingpostalcode || '',
      countryCode: h.shippingcountry || 'US',
      residential: true,
      email: (shipToEmailOverride || h.shippingemail || '').trim(),
      phone: (shipToPhoneOverride || h.shippingphone || '').trim(),
    };
    return { shipper, shipTo };
  }, [header, appCfg, clientName, shipToPhoneOverride, shipToEmailOverride]);
  // Initialize default bundle from items after items load
  useEffect(() => {
    if (!items.length) return;
    const nonOwn = items.filter(row => !flagOwnContainer(row));
    const initialItems = nonOwn.map(x => ({
      id: x.id,
      qty: Number(x.qty_ordered || x.qty || 0) || 0
    })).filter(x => x.qty > 0);
    const estW = +nonOwn.reduce((s, r) => s + rowWeightLbsRaw(r) * (Number(r.qty_ordered || r.qty || 0) || 0), 0).toFixed(2);
    setBundlePackages([{ id: 1, weight: estW || '', unit: 'LBS', length: '', width: '', height: '', dimUnit: 'IN', selectedShipPkgId: '', items: initialItems }]);
    setBundleSelections({});
  }, [items]);
  // ===== Recommendation per bundle (volume and dimensions need + suggested box) =====
  const bundleRecommendations = useMemo(() => {
    const map = new Map(); // bundleId -> {needCuFt, rec: shipPkg| null}
    const volHeadroom = 1.10;
    const dimHeadroom = 1.05;
    for (const b of bundlePackages) {
      // Aggregate volume need and prep dims
      let need = 0;
      const dimsList = []; // {L,W,H, qty}
      for (const it of (b.items || [])) {
        const row = itemById.get(it.id);
        const qty = Number(it.qty || 0) || 0;
        if (!row || qty <= 0) continue;
        need += itemVolumeFt(row) * qty;
        const [L,W,H] = itemDimsIn(row);
        if (L>0 && W>0 && H>0) dimsList.push({ L, W, H, qty });
      }
      need = +(need * volHeadroom).toFixed(3);

      // Build simple stacking heuristics: stack along H, along L, along W
      // H-stack: reqL=max(L), reqW=max(W), reqH=sum(H*qty)
      // L-stack: reqL=sum(L*qty), reqW=max(W), reqH=max(H)
      // W-stack: reqL=max(L), reqW=sum(W*qty), reqH=max(H)
      const reqs = [];
      if (dimsList.length) {
        const maxL = Math.max(...dimsList.map(d => d.L));
        const maxW = Math.max(...dimsList.map(d => d.W));
        const maxH = Math.max(...dimsList.map(d => d.H));
        const sumL = dimsList.reduce((s,d)=>s + d.L * d.qty, 0);
        const sumW = dimsList.reduce((s,d)=>s + d.W * d.qty, 0);
        const sumH = dimsList.reduce((s,d)=>s + d.H * d.qty, 0);
        reqs.push([maxL, maxW, sumH]); // stack along H
        reqs.push([sumL, maxW, maxH]); // stack along L
        reqs.push([maxL, sumW, maxH]); // stack along W
      }

      let rec = null;
      if (shipPkgOptions.length) {
        const clientFirst = shipPkgOptions.filter(x => x.clientid != null).concat(shipPkgOptions.filter(x => x.clientid == null));
        // Iterate boxes by priority then by volume; choose first that satisfies both volume and one dims heuristic
        rec = clientFirst.find(box => {
          if (!(box.volume_ft != null && box.volume_ft >= need)) return false;
          const boxDims = [Number(box.length||0), Number(box.width||0), Number(box.height||0)];
          if (boxDims.some(n => !(n>0))) return false;
          if (!reqs.length) return true; // no item dims -> rely on volume only
          return reqs.some(r => fitsDims(r, boxDims, dimHeadroom));
        }) || null;
        // Fallback: if none matches both, take the first that matches dims alone
        if (!rec) {
          rec = clientFirst.find(box => {
            const boxDims = [Number(box.length||0), Number(box.width||0), Number(box.height||0)];
            if (boxDims.some(n => !(n>0))) return false;
            return reqs.some(r => fitsDims(r, boxDims, dimHeadroom));
          }) || null;
        }
        // Last resort: largest available
        if (!rec) rec = clientFirst.at(-1) || null;
      }
      map.set(b.id, { needCuFt: need, rec });
    }
    return map;
  }, [bundlePackages, shipPkgOptions, itemById]);
  // ===== Selection helpers (qty-aware) =====
  const getSelectionMap = (bundleId) => bundleSelections[bundleId] || new Map();
  const setSelectionMap = (bundleId, map) =>
    setBundleSelections(prev => ({ ...prev, [bundleId]: new Map(map) }));
  const toggleBundleItem = (bundleId, itemId, maxQty) => {
    const cur = getSelectionMap(bundleId);
    const next = new Map(cur);
    if (next.has(itemId)) next.delete(itemId);
    else next.set(itemId, Math.min(1, Math.max(1, Number(maxQty) || 1)));
    setSelectionMap(bundleId, next);
  };
  const setMoveQty = (bundleId, itemId, val, maxQty) => {
    const n = Math.max(1, Math.min(Number(val) || 1, Number(maxQty) || 1));
    const cur = getSelectionMap(bundleId);
    if (!cur.has(itemId)) return;
    const next = new Map(cur);
    next.set(itemId, n);
    setSelectionMap(bundleId, next);
  };
  const selectAllInBundle = (bundleId) => {
    const b = bundlePackages.find(x => x.id === bundleId);
    if (!b) return;
    const next = new Map();
    for (const it of b.items || []) {
      if (it.qty > 0) next.set(it.id, it.qty);
    }
    setSelectionMap(b.id, next);
  };
  const clearAllInBundle = (bundleId) => setSelectionMap(bundleId, new Map());
  const setBundleWeight = (bundleId, w) =>
    setBundlePackages(prev => prev.map(b => b.id === bundleId ? { ...b, weight: w } : b));
  const setBundleDim = (bundleId, key, val) =>
    setBundlePackages(prev => prev.map(b => (b.id === bundleId ? { ...b, [key]: val } : b)));
  const setBundleSelectedPkg = (bundleId, pkgId) => {
    setBundlePackages(prev => prev.map(b => {
      if (b.id !== bundleId) return b;
      const pkg = shipPkgOptions.find(p => String(p.id) === String(pkgId));
      if (!pkg) return { ...b, selectedShipPkgId: '' };
      return {
        ...b,
        selectedShipPkgId: String(pkg.id),
        length: pkg.length || '',
        width: pkg.width || '',
        height: pkg.height || '',
        dimUnit: (pkg.dim_unit || 'IN').toUpperCase()
      };
    }));
  };
  // Flatten package items to id list (repeat per qty)
  const flattenBundleItemIds = (bundle) => {
    const out = [];
    for (const it of bundle.items || []) {
      for (let i = 0; i < (Number(it.qty) || 0); i++) out.push(it.id);
    }
    return out;
  };
  // ===== Packages to rate/purchase (includes dimensions) =====
  const packageList = useMemo(() => {
    const result = [];
    // OSC: one package per unit; dimensions from item (if available)
    for (const r of ownContainerItems) {
      const qty = Number(r.qty_ordered || r.qty || 0) || 0;
      const perUnitWeight = Math.max(1, rowWeightLbs(r) || 0);
      const L = Number(r.length ?? 0);
      const W = Number(r.width ?? 0);
      const H = Number(r.height ?? 0);
      const itemDims = (L > 0 && W > 0 && H > 0) ? { length: L, width: W, height: H, unit: 'IN' } : undefined;
      for (let i = 0; i < qty; i++) {
        result.push({
          key: `own-${r.id}-${i + 1}`,
          weight: perUnitWeight,
          unit: 'LBS',
          source: 'own',
          items: [r.id],
          label: `${r.sku || r.itemdescription || `Item ${r.id}`} #${i + 1}`,
          dimensions: itemDims
        });
      }
    }
    // User-assembled packages: include only if any items selected (avoid empty placeholder)
    for (const b of bundlePackages) {
      const itemIds = flattenBundleItemIds(b);
      if (!itemIds.length) continue;
      const dims =
        Number(b.length) > 0 && Number(b.width) > 0 && Number(b.height) > 0
          ? { length: Number(b.length), width: Number(b.width), height: Number(b.height), unit: b.dimUnit || 'IN' }
          : undefined;
      const selPkg = shipPkgOptions.find(p => String(p.id) === String(b.selectedShipPkgId));
      result.push({
        key: `package-${b.id}`,
        weight: Math.max(1, Number(b.weight || 0) || 0),
        unit: b.unit || 'LBS',
        source: 'bundle',
        items: itemIds,
        label: `Package ${b.id}`,
        dimensions: dims,
        packageCode: selPkg?.code || undefined,
      });
    }
    return result;
  }, [ownContainerItems, bundlePackages, weightOverrides, shipPkgOptions]);

  // Detect overweight against provider constraints (currently UPS)

  // Auto-split a single bundle by weight into multiple <= limit (greedy by item weights)
  const splitBundleByWeight = (bundleId, maxLbs = UPS_MAX_LBS) => {
    const idx = bundlePackages.findIndex(x => x.id === bundleId);
    if (idx === -1) return;
    const src = bundlePackages[idx];
    const srcItems = (src.items || []).slice();
    if (!srcItems.length) return;
    // Build unit list [{id, w}] expanded by qty
    let totalQty = 0;
    const expanded = [];
    for (const it of srcItems) {
      const row = itemById.get(it.id);
      const unitW = Math.max(0, Number(rowWeightLbsRaw(row) || 0));
      const qty = Math.max(0, Number(it.qty || 0));
      totalQty += qty;
      for (let i = 0; i < qty; i++) expanded.push({ id: it.id, w: unitW });
    }
    if (!expanded.length) return;
    // If some items have 0 weight, approximate using avg if total bundle weight known
    const enteredW = Number(src.weight || 0) || 0;
    const knownSum = expanded.reduce((s, u) => s + (u.w || 0), 0);
    if (enteredW > knownSum && expanded.some(u => u.w === 0)) {
      const missing = expanded.filter(u => u.w === 0);
      const addEach = (enteredW - knownSum) / missing.length;
      for (const m of missing) m.w = Math.max(0, addEach);
    }
    // Greedy FFD: sort descending by w
    expanded.sort((a, b) => b.w - a.w);
    const packs = [];
    for (const u of expanded) {
      // If a single unit exceeds max, cannot split for UPS
      if (u.w > maxLbs) {
        alert('An item exceeds the per-package UPS limit of 150 lbs. Consider another carrier or service.');
        return;
      }
      let placed = false;
      for (const p of packs) {
        if ((p.total + u.w) <= maxLbs) {
          p.total += u.w;
          p.units.push(u);
          placed = true;
          break;
        }
      }
      if (!placed) packs.push({ total: u.w, units: [u] });
    }
    // Transform packs -> bundle entries
    const nextBundles = packs.map((p, i) => {
      // Count per id
      const counts = new Map();
      for (const u of p.units) counts.set(u.id, (counts.get(u.id) || 0) + 1);
      return {
        id: (bundlePackages.at(-1)?.id || 0) + 1 + i,
        weight: +p.total.toFixed(2),
        unit: src.unit || 'LBS',
        length: src.length || '',
        width: src.width || '',
        height: src.height || '',
        dimUnit: src.dimUnit || 'IN',
        selectedShipPkgId: src.selectedShipPkgId || '',
        items: Array.from(counts.entries()).map(([id, qty]) => ({ id, qty }))
      };
    });
    // Commit: replace src with first of nextBundles, append the rest
    setBundlePackages(prev => {
      const head = nextBundles[0];
      const tail = nextBundles.slice(1);
      const cp = prev.slice();
      cp[idx] = head;
      // Avoid id collisions by ensuring strictly increasing ids
      const maxId = cp.reduce((m, b) => Math.max(m, b.id), 0);
      let curId = maxId;
      for (const b of tail) cp.push({ ...b, id: ++curId });
      return cp;
    });
    // Clear selections for affected bundle(s)
    setBundleSelections(prev => ({ ...prev, [bundleId]: new Map() }));
  };

  // Auto-split all overweight bundles for UPS
  const autoSplitOverweightBundles = (maxLbs = UPS_MAX_LBS) => {
    if (!overweightInfo?.bundles?.length) return;
    for (const b of overweightInfo.bundles) splitBundleByWeight(b.id, maxLbs);
  };
  const selectedProvider = useMemo(
    () => providers.find(p => String(p.id) === String(providerId)) || null,
    [providers, providerId]
  );
  const supportsHomeDelivery = useMemo(() => {
    const s = String(selectedProvider?.servicetype || '').toLowerCase();
    return isUPS(s) || isFedEx(s) || isShipEngine(s); // both support residential flags
  }, [selectedProvider]);
  const supportsSignature = useMemo(() => {
    const s = String(selectedProvider?.servicetype || '').toLowerCase();
    return isUPS(s) || isFedEx(s) || isShipEngine(s); // both have signature options (wire in backend later)
  }, [selectedProvider]);
  const overweightInfo = useMemo(() => {
    const info = { own: [], bundles: [] };
    const isUpsProv = isUPS(selectedProvider?.servicetype || '');
    if (!isUpsProv) return info;
    for (const r of ownContainerItems) {
      const unit = rowWeightLbs(r) || 0;
      if (unit > UPS_MAX_LBS) info.own.push({ id: r.id, sku: r.sku, unit });
    }
    for (const b of bundlePackages) {
      const w = Number(b.weight || 0) || 0;
      const hasItems = (b.items || []).length > 0;
      if (hasItems && w > UPS_MAX_LBS) info.bundles.push({ id: b.id, weight: w });
    }
    return info;
  }, [ownContainerItems, bundlePackages, weightOverrides, selectedProvider]);
  const normalizeRates = (json) => {
    console.log('normalizeRates input:', json); // Debug the raw API response
    if (Array.isArray(json?.rates)) return json.rates.map(r => ({
      ...r,
      rateId: r.rate_id || r.rateId || undefined,
      packageType: r.package_type || undefined, // Prioritize package_type from response
      packageCode: r.package_code || r.packageCode || undefined,
    }));
    const seRates = Array.isArray(json) ? json
      : Array.isArray(json?.rate_response?.rates) ? json.rate_response.rates
        : Array.isArray(json?.rates) ? json.rates : [];
    console.log('normalized rates:', seRates); // Debug the rates before mapping
    return seRates.map(r => ({
      serviceCode: r.service_code || r.service_type,
      serviceName: r.service_type || r.service_code || 'Service',
      totalCharges: Number(r?.shipping_amount?.amount ?? 0) + Number(r?.other_amount?.amount ?? 0),
      currency: r?.shipping_amount?.currency || 'USD',
      rateId: r?.rate_id || undefined,
      packageType: r.package_type || undefined, // Ensure package_type is captured
      packageCode: r.package_code || undefined,
    }));
  };
  // Validate all user-assembled packages that contain items have required dimensions
  function validateBundleDimensions() {
    for (const b of bundlePackages) {
      const hasItems = Array.isArray(b.items) && b.items.length > 0;
      const L = Number(b.length), W = Number(b.width), H = Number(b.height);
      if (hasItems && (!(L > 0) || !(W > 0) || !(H > 0))) {
        setStatusMsg(`Please enter dimensions (L×W×H) for Package ${b.id}.`);
        return false;
      }
    }
    return true;
  }
  const requestRates = async () => {
    setRatesError(''); setRates([]);
    if (ownMissingWeights.length > 0) return;
    const bundlesNeedWeight = bundlePackages.some(b => (b.items || []).length > 0 && !(Number(b.weight) > 0));
    if (bundlesNeedWeight) { setStatusMsg('Please enter weight for all user-assembled packages.'); return; }
    if (!validateBundleDimensions()) return;
    // Preflight: block UPS if any package exceeds 150 lbs
    if (isUPS(selectedProvider?.servicetype || '')) {
      if (overweightInfo.own.length) {
        setRatesError('UPS: One or more own-container items exceed 150 lbs per package. Reduce weight or choose a freight/alternate service.');
        return;
      }
      if (overweightInfo.bundles.length) {
        setRatesError('UPS: One or more packages exceed 150 lbs. Use Auto-split to split into multiple packages.');
        return;
      }
    }
    setFetchingRates(true);
    try {
      if (!selectedProvider?.routeBase) throw new Error('Pick a provider first');
      const id = encodeURIComponent(selectedProvider.id);
      const base = selectedProvider.routeBase;
      const url = `${API}${base}/${id}/rates`;
      const body = {
        shipper: {
          name: shipAddrs.shipper.name,
          phone: sanitizePhone(shipAddrs.shipper.phone),
          accountNumber: shipAddrs.shipper.accountNumber || undefined,
          address: {
            postalCode: shipAddrs.shipper.address.postalCode,
            countryCode: shipAddrs.shipper.address.countryCode || 'US',
            address1: shipAddrs.shipper.address.address1,
            address2: shipAddrs.shipper.address.address2 || undefined,
            city: shipAddrs.shipper.address.city,
            state: shipAddrs.shipper.address.state || undefined,
            residential: !!shipAddrs.shipper.address.residential,
          },
        },
        shipTo: {
          name: shipAddrs.shipTo.name,
          phone: sanitizePhone(shipAddrs.shipTo.phone),
          email: shipAddrs.shipTo.email || undefined,
          address: {
            postalCode: shipAddrs.shipTo.postalCode,
            countryCode: shipAddrs.shipTo.countryCode || 'US',
            address1: shipAddrs.shipTo.address1,
            address2: shipAddrs.shipTo.address2 || undefined,
            city: shipAddrs.shipTo.city,
            state: shipAddrs.shipTo.state || undefined,
            residential: !!homeDelivery, // <-- NEW: Home Delivery toggle
          },
        },
        shipDate,
        deliveryOptions: {
          homeDelivery: !!homeDelivery,
          signatureRequired: !!signatureRequired,
        },
        packages: packageList.map(p => ({
          weight: { unit: p.unit || 'LBS', value: Number(p.weight || 1) },
          items: p.items,
          dimensions: p.dimensions ? {
            unit: p.dimensions.unit || 'IN',
            length: Number(p.dimensions.length),
            width: Number(p.dimensions.width),
            height: Number(p.dimensions.height),
          } : undefined,
          packageCode: p.packageCode || undefined,
        })),
        eorderId,
        forceHardRates: true,
      };
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || 'Failed to fetch rates');
      const list = normalizeRates(j).sort((a, b) => Number(a.totalCharges || 0) - Number(b.totalCharges || 0));
      console.log('final rates set:', list); // Debug the final rates
      setRates(list);
    } catch (e) {
      setRatesError(e.message || 'Failed to fetch rates');
    } finally {
      setFetchingRates(false);
    }
  };
  const normalizeLabelsFromPurchase = (purchaseJson) => {
    if (!purchaseJson) return [];
    const srcArray = Array.isArray(purchaseJson.labels)
      ? purchaseJson.labels
      : Array.isArray(purchaseJson) ? purchaseJson : [purchaseJson];
    return srcArray.map((r) => {
      const trackingNumber = r.trackingNumber ?? r.tracking_number ?? r.tracking ?? purchaseJson?.trackingNumber ?? null;
      const carrierCode    = r.carrier ?? r.carrierCode ?? purchaseJson?.carrierCode ?? null;

      // Charges (ensure we carry backend-computed upcharges)
      const carrierCharges = r.carrierCharges ?? r.carrier_charges ?? purchaseJson?.carriercharges ?? 0;
      const partnerCharges = r.partnerCharges ?? r.partner_charges ?? purchaseJson?.partnercharges ?? 0;
      const juicyCharges   = r.juicyCharges   ?? r.juicy_charges   ?? purchaseJson?.juicycharges   ?? 0;
      const threeplCharges = r.threeplCharges ?? r.threepl_charges ?? purchaseJson?.threeplcharges ?? 0;
      const totalCharges   = r.totalCharges   ?? r.total_charges   ?? purchaseJson?.totalcharges   ?? 0;

      const currency = r.currency ?? purchaseJson?.currency ?? 'USD';
      const rateId   = r.rateId ?? r.rate_id ?? purchaseJson?.rateId ?? undefined;

      const pdfUrl   = r.pdfUrl  ?? r.labelPdfUrl  ?? r?.label_download?.pdf   ?? purchaseJson?.pdfUrl  ?? undefined;
      const pngUrl   = r.pngUrl  ?? r.labelPngUrl  ?? r?.label_download?.png   ?? purchaseJson?.pngUrl  ?? undefined;
      const pdfBase64 = r.pdfBase64 ?? purchaseJson?.pdfBase64 ?? undefined;
      const pngBase64 = r.pngBase64 ?? purchaseJson?.pngBase64 ?? undefined;
      const labelType = r.labelType ?? 1;
      const alreadySplit = r.alreadySplit ?? purchaseJson?.alreadySplit ?? false;

      return {
        trackingNumber,
        carrierCode,
        carrierCharges: Number(carrierCharges),
        partnerCharges: Number(partnerCharges),
        juicyCharges: Number(juicyCharges),
        threeplCharges: Number(threeplCharges),
        totalCharges: Number(totalCharges),
        currency,
        rateId,
        pdfUrl, pngUrl, pdfBase64, pngBase64,
        labelType,
        // Hint to backend that these monetary fields are already per-label
        alreadySplit: !!alreadySplit,
        shippingIntegrationId: Number(selectedProvider?.id) || 0,
      };
    });
  };
  const persistLabel = async (_providerId, purchaseJson) => {
    const labelsPayload = normalizeLabelsFromPurchase(purchaseJson);
    const res = await fetch(`${API}/api/eorders/${eorderId}/labels`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shippingIntegrationId: Number(selectedProvider?.id) || 0,
        labels: labelsPayload
      }),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || j?.success === false) {
      throw new Error(j?.error || 'Failed to store label');
    }
    const labelIds = Array.isArray(j?.results) ? j.results.map(r => r.id).filter(Boolean) : [];
    return { labelIds, stored: j };
  };
  async function persistShipments({ eorderId, labelIds = [], selectedFolId = 0, packageQtyByIndex = {}, runId = null }) {
    const id = Number(eorderId);
    if (!Number.isFinite(id) || id <= 0) throw new Error('Invalid eorder id');
    if (!Array.isArray(labelIds) || labelIds.length === 0) throw new Error('No labelIds to create shipments for');
    const shipments = labelIds.map((labelId, idx) => {
      const packageIndex = idx + 1;
      return {
        labelId,
        fulfillment_order_line_id: Number(selectedFolId) || 0,
        fulfillment_id: 0,
        qty: Number(packageQtyByIndex[packageIndex] || 1),
        package_no: packageIndex,
      };
    });
    const res = await fetch(`${API}/api/eorders/${id}/shipments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ run_id: runId || undefined, shipments }),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || j?.success === false) throw new Error(j?.error || 'Failed to save shipments');
    return { success: true, createdShipments: j.results || [] };
  }
  async function savePackagingStructure() {
    const body = {
      note: 'Pre-purchase packaging',
      created_by: (JSON.parse(localStorage.getItem('user') || '{}')?.fullname) || 'system',
      packages: packageList.map((p, idx) => ({
        package_no: idx + 1,
        source: p.source === 'own' ? 'own' : 'bundle',
        weight: { value: Number(p.weight || 0), unit: p.unit || 'LBS' },
        dimensions: p.dimensions ? {
          length: p.dimensions.length,
          width: p.dimensions.width,
          height: p.dimensions.height,
          unit: p.dimensions.unit || 'IN'
        } : null,
        items: (p.items || []).map(itemId => {
          const row = itemById.get(itemId);
          return {
            eorder_line_item_id: itemId,
            qty: 1,
            sku: row?.sku || null,
            description: row?.itemdescription || null,
            unit_weight_lbs: rowWeightLbsRaw(row) || null,
          };
        })
      })),
    };
    const resp = await fetch(`${API}/api/eorders/${eorderId}/packaging`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const j = await resp.json().catch(() => ({}));
    if (!resp.ok || j?.success === false) throw new Error(j?.error || 'Failed to save packaging structure');
    return j; // { success, run_id, run_seq }
  }
  const printLabels = async (rateChoice) => {
    if (ownMissingWeights.length > 0) return;
    const bundlesNeedWeight = bundlePackages.some(b => (b.items || []).length > 0 && !(Number(b.weight) > 0));
    if (bundlesNeedWeight) { setStatusMsg('Please enter weight for all user-assembled packages.'); return; }
    if (!validateBundleDimensions()) return;
    try {
      if (!selectedProvider?.routeBase) {
        alert('Choose a shipping provider first.');
        return;
      }
      setPurchasing(true);
      setStatusMsg('Saving packaging structure…');
      const packRes = await savePackagingStructure();
      const runId = packRes?.run_id || null;
      const runSeq = packRes?.run_seq || null;
      const providerIdStr = String(selectedProvider.id);
      const providerId = encodeURIComponent(selectedProvider.id);
      const routeBase = selectedProvider.routeBase;
      // Preflight: check shipping funds BEFORE purchase based on the selected rate
      try {
        const baseTotal = Number(rateChoice?.totalCharges || 0);
        if (!(baseTotal > 0)) throw new Error('Missing total charges for selected rate');
        setStatusMsg('Checking shipping funds…');
        const preResp = await fetch(`${API}/api/eorders/${eorderId}/labels/funding-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            totalCharges: baseTotal,
            providerId: Number(selectedProvider?.id) || undefined,
            signatureRequired: !!signatureRequired,
          }),
        });
        const preJson = await preResp.json().catch(() => ({}));
        if (!preResp.ok) {
          throw new Error(preJson?.error || 'Insufficient shipping funds');
        }
      } catch (err) {
        setStatusMsg(`Error: ${err.message}`);
        setPurchasing(false);
        return;
      }
      const purchaseUrl = `${API}${routeBase}/${providerId}/labels/for-eorder`;
      const purchaseBody = {
        eorderId,
        providerId: providerIdStr,
        rateId: rateChoice?.rateId || undefined,
        serviceCode: rateChoice?.serviceCode || undefined,
        shipDate,
        shipper: {
          name: shipAddrs.shipper.name,
          phone: sanitizePhone(shipAddrs.shipper.phone),
          address: {
            postalCode: shipAddrs.shipper.address.postalCode,
            countryCode: shipAddrs.shipper.address.countryCode || 'US',
            address1: shipAddrs.shipper.address.address1,
            address2: shipAddrs.shipper.address.address2 || undefined,
            city: shipAddrs.shipper.address.city,
            state: shipAddrs.shipper.address.state || undefined,
            residential: !!shipAddrs.shipper.address.residential,
          },
        },
        shipTo: {
          name: shipAddrs.shipTo.name,
          phone: sanitizePhone(shipAddrs.shipTo.phone),
          email: shipAddrs.shipTo.email || undefined,
          address: {
            postalCode: shipAddrs.shipTo.postalCode,
            countryCode: shipAddrs.shipTo.countryCode || 'US',
            address1: shipAddrs.shipTo.address1,
            address2: shipAddrs.shipTo.address2 || undefined,
            city: shipAddrs.shipTo.city,
            state: shipAddrs.shipTo.state || undefined,
            residential: !!homeDelivery, // <-- NEW: Home Delivery toggle
          },
        },
        deliveryOptions: {
          homeDelivery: !!homeDelivery,
          signatureRequired: !!signatureRequired,
        },
        packages: packageList.map(p => ({
          weight: { unit: p.unit || 'LBS', value: Number(p.weight || 1) },
          items: p.items,
          dimensions: p.dimensions ? {
            unit: p.dimensions.unit || 'IN',
            length: Number(p.dimensions.length),
            width: Number(p.dimensions.width),
            height: Number(p.dimensions.height),
          } : undefined,
          label: p.label,
          packageCode: p.packageCode || undefined,
        })),
      };
      setStatusMsg('Purchasing label(s)…');
      const purchaseRes = await fetch(purchaseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(purchaseBody),
      });
      const purchaseJson = await purchaseRes.json().catch(() => ({}));
      if (!purchaseRes.ok || purchaseJson?.success === false) {
        throw new Error(purchaseJson?.error || 'Label purchase failed');
      }
      setStatusMsg('Saving label metadata…');
      const { labelIds } = await persistLabel(providerIdStr, purchaseJson);
      if (!Array.isArray(labelIds) || labelIds.length === 0) {
        throw new Error('No labels were stored for this purchase');
      }
      setStatusMsg('Creating shipment records…');
      await persistShipments({
        eorderId,
        labelIds,
        selectedFolId: 0,
        packageQtyByIndex: {},
        runId,
      });
      if (runId) {
        await fetch(`${API}/api/eorders/${eorderId}/packaging/${runId}/mark-used`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ exclusive: true })
        });
      }
      window.dispatchEvent(new CustomEvent('eorders:refresh'));
      setStatusMsg(`Done.${runSeq ? ` (Packing Run #${runSeq})` : ''}`);
      onClose && onClose();
    } catch (e) {
      setStatusMsg(`Error: ${e.message}`);
    } finally {
      setPurchasing(false);
    }
  };
  // ===== UI =====
  return (
    <div className="elbl-overlay">
      <div className="elbl-modal" onClick={(e) => e.stopPropagation()}>
        <div className="elbl-head">
          <h3>
            <FaPrint style={{ verticalAlign: '-2px', marginRight: 6 }} />
            Print Shipping Labels — {clientName ? `${clientName} • ` : ''}Order {orderNumber ? `#${orderNumber}` : eorderId}
            {statusMsg?.includes('Packing Run #') && (
              <span style={{ marginLeft: 8, fontSize: 12, background: '#1f2937', color: '#fff', borderRadius: 999, padding: '2px 8px' }}>
                {statusMsg.match(/Packing Run #\d+/)?.[0]}
              </span>
            )}
          </h3>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><FaTimes /></button>
        </div>
        {(purchasing || statusMsg) && (
          <div className="elbl-status" role="status" aria-live="polite">
            {statusMsg}
          </div>
        )}
        <div className="elbl-body">
          {/* UPS overweight warning & auto-split controls */}
          {isUPS(selectedProvider?.servicetype || '') && (overweightInfo.own.length || overweightInfo.bundles.length) ? (
            <div className="elbl-warn">
              <FaExclamationTriangle style={{ marginRight: 8 }} />
              UPS limit is 150 lbs per package.{' '}
              {overweightInfo.own.length ? `Own-container SKU(s) over limit: ${overweightInfo.own.map(o => o.sku || `#${o.id}`).join(', ')}. ` : ''}
              {overweightInfo.bundles.length ? (
                <>
                  Overweight package(s): {overweightInfo.bundles.map(b => `#${b.id} (${b.weight} lbs)`).join(', ')}.{' '}
                  <button
                    className="btn ghost"
                    onClick={() => autoSplitOverweightBundles()}
                    disabled={purchasing}
                    style={{ marginLeft: 8 }}
                  >
                    Auto-split to {'<='} 150 lbs
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
          {(ownMissingWeights.length > 0 || missingBundleWeights || missingBundleDims) && (
            <div className="elbl-warn">
              <FaExclamationTriangle style={{ marginRight: 8 }} />
              Some items/packages are missing details. Enter weights and dimensions (L×W×H) for all packages.
            </div>
          )}
          <div className="elbl-row" style={{ flexWrap: 'nowrap', alignItems: 'flex-end' }}>
            <label className="elbl-field" style={{ minWidth: 220, flex: '0 0 auto' }}>
              <span>Provider</span>
              {loadingProviders ? (
                <div>Loading…</div>
              ) : (
                <div className="elbl-provider-select">
                  {selectedProvider?.icon && (
                    <img
                      src={selectedProvider.icon}
                      alt=""
                      style={{
                        width: 32,
                        height: 32,
                        marginRight: 8,
                        verticalAlign: 'middle',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                  <select
                    value={providerId}
                    onChange={(e) => setProviderId(e.target.value)}
                    disabled={purchasing}
                  >
                    <option value="">— Select —</option>
                    {providers.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {(p.displayname || `#${p.id}`)} — {p.servicetype}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </label>
            <label className="elbl-field" style={{ minWidth: 220, flex: '0 0 auto' }}>
              <span>Ship Date</span>
              <input type="date" value={shipDate} onChange={(e) => setShipDate(e.target.value)} disabled={purchasing} />
            </label>
            <div className="elbl-field" style={{ alignSelf: 'end', flex: '0 0 auto' }}>
              <button
                className="btn"
                onClick={requestRates}
                disabled={
                  fetchingRates || !providerId || purchasing || loadingCfg ||
                  ownMissingWeights.length > 0 || missingBundleWeights || missingBundleDims
                }
                title={
                  ownMissingWeights.length > 0 ? 'Some own-container items are missing weight' :
                  missingBundleWeights ? 'Enter weight for all user-assembled packages' :
                  missingBundleDims ? 'Enter L×W×H for all user-assembled packages' : ''
                }
              >
                {fetchingRates ? 'Getting Rates…' : 'Get Rates'}
              </button>
            </div>
          </div>
          <div className="elbl-split">
            {/* LEFT: OSC + Packaged Shipments */}
            <div className="elbl-left">
              {/* OSC */}
              <fieldset className="elbl-card">
                <legend>Items — Own Shipping Container</legend>
                {loadingItems ? (
                  <div className="elbl-empty">Loading…</div>
                ) : ownContainerItems.length ? (
                  <table className="elbl-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Description</th>
                        <th>Dims (L×W×H in)</th>
                        <th className="num">Unit Wgt (lbs)</th>
                        <th className="num">Qty (labels)</th>
                        <th className="num">Total Wgt (lbs)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownContainerItems.map(r => {
                        const missing = (rowWeightLbs(r) || 0) <= 0;
                        const qty = Number(r.qty_ordered || r.qty || 0) || 0;
                        const unit = rowWeightLbs(r);
                        const total = (unit && qty) ? +(unit * qty).toFixed(2) : '';
                        return (
                          <tr key={`own-${r.id}`} className={missing ? 'row-missing' : ''}>
                            <td>{r.sku}</td>
                            <td>{r.itemdescription}</td>
                            <td>{rowDims(r)}</td>
                            <td className="num">
                              {missing ? (
                                <input
                                  className="elbl-input small"
                                  type="number" min="0" step="0.1"
                                  placeholder="Enter weight"
                                  value={weightOverrides[r.id] ?? ''}
                                  onChange={(e) => setWeightOverrides(prev => ({ ...prev, [r.id]: e.target.value }))}
                                  disabled={purchasing}
                                />
                              ) : unit}
                            </td>
                            <td className="num">{qty}</td>
                            <td className="num">{total || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (<div className="elbl-empty">No items flagged as Own Shipping Container.</div>)}
              </fieldset>
              {/* Packaged Shipments */}
              <fieldset className="elbl-card">
                <legend>Packaged Shipments</legend>
                {bundlePackages.map((b) => {
                  const sel = getSelectionMap(b.id);
                  const bItems = (b.items || []).map(it => ({ ...it, row: itemById.get(it.id) })).filter(x => x.row);
                  const anyMissingWeight = !(Number(b.weight) > 0) && bItems.length > 0;
                  const isOverLimit = isUPS(selectedProvider?.servicetype || '') && Number(b.weight || 0) > UPS_MAX_LBS && bItems.length > 0;
                  const rec = bundleRecommendations.get(b.id);
                  return (
                    <div key={b.id} className={`elbl-bundle ${anyMissingWeight ? 'row-missing' : ''}`}>
                      <div className="elbl-bundle-head" style={{ gap: 12 }}>
                        <div className="elbl-bundle-title">
                          <FaBoxes style={{ marginRight: 6 }} /> Package {b.id}
                        </div>
                        <button
                          className="btn ghost"
                          onClick={() =>
                            setBundlePackages(prev => prev.length > 1 ? prev.filter(x => x.id !== b.id) : prev)
                          }
                          disabled={(bundlePackages.length <= 1) || purchasing}
                          title="Remove package"
                        >
                          <FaMinus />
                        </button>
                      </div>
                      {/* Tools */}
                      <div className="elbl-bundle-tools" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '6px 0' }}>
                        <button
                          className="btn"
                          onClick={() => {
                            const m = new Map();
                            bItems.forEach(it => { if (it.qty > 0) m.set(it.id, it.qty); });
                            setSelectionMap(b.id, m);
                          }}
                          disabled={!bItems.length || purchasing}
                        >
                          Select All
                        </button>
                        <button className="btn" onClick={() => clearAllInBundle(b.id)} disabled={!sel.size || purchasing}>
                          Clear
                        </button>
                        {isOverLimit && (
                          <button
                            className="btn warning"
                            onClick={() => splitBundleByWeight(b.id)}
                            disabled={purchasing}
                            title="Auto-split this package into multiple <=150 lb packages"
                          >
                            Auto-split ≤150 lbs (UPS)
                          </button>
                        )}
                        <button
                          className="btn"
                          onClick={() => {
                            // Validate dims if splitting a non-empty package
                            const L = Number(b.length), W = Number(b.width), H = Number(b.height);
                            if (bItems.length > 0 && (!(L > 0) || !(W > 0) || !(H > 0))) {
                              alert(`Please enter dimensions for Package ${b.id} before splitting.`);
                              return;
                            }
                            // Find source bundle
                            const idx = bundlePackages.findIndex(x => x.id === b.id);
                            if (idx === -1) return;
                            const src = bundlePackages[idx];
                            // Selected quantities to move
                            const curSel = getSelectionMap(b.id);
                            if (!curSel.size) { alert('Select SKUs (and quantities) to split.'); return; }
                            // Build remaining (with reduced qty) and moved lists (numeric-safe)
                            const remaining = [];
                            const moved = [];
                            let movedWeight = 0;
                            for (const it of (src.items || [])) {
                              const have = Number(it.qty) || 0;
                              const req  = Number(curSel.get(it.id)) || 0;
                              const moveQty = Math.min(req, have);
                              if (moveQty > 0) {
                                moved.push({ id: it.id, qty: moveQty });
                                const row = itemById.get(it.id);
                                const per = row ? Number(rowWeightLbsRaw(row)) || 0 : 0;
                                movedWeight += per * moveQty;
                                const rem = have - moveQty;
                                if (rem > 0) {
                                  // IMPORTANT: push a NEW object with the reduced qty so Qty In Package updates visually
                                  remaining.push({ id: it.id, qty: rem });
                                }
                                // if rem === 0, omit from remaining (fully moved)
                              } else {
                                // unchanged line item stays as-is
                                remaining.push({ id: it.id, qty: have });
                              }
                            }
                            if (!moved.length) { alert('Nothing to split.'); return; }
                            const nextId = (bundlePackages.at(-1)?.id || 0) + 1;
                            const newWeight    = movedWeight ? +movedWeight.toFixed(2) : '';
                            const sourceWeight = Number(src.weight || 0) || 0;
                            const newSrcWeight = Math.max(0, +((sourceWeight) - movedWeight).toFixed(2));
                            // Commit: update source (with reduced qty), add new bundle (with moved qty)
                            setBundlePackages(prev => {
                              const cp = prev.slice();
                              cp[idx] = { ...src, items: remaining, weight: newSrcWeight };
                              cp.push({
                                id: nextId,
                                weight: newWeight,
                                unit: src.unit || 'LBS',
                                length: src.length || '',
                                width: src.width || '',
                                height: src.height || '',
                                dimUnit: src.dimUnit || 'IN',
                                selectedShipPkgId: '',
                                items: moved
                              });
                              return cp;
                            });
                            // Clear selections for both bundles to avoid stale UI
                            setBundleSelections(prev => ({ ...prev, [b.id]: new Map(), [nextId]: new Map() }));
                          }}
                          disabled={!sel.size || purchasing}
                        >
                          Split / Move Selected Into New Package
                        </button>
                      </div>
                      {/* Items in package */}
                      <div className="elbl-bundle-items">
                        {bItems.length ? (
                          <table className="elbl-table compact">
                            <thead>
                              <tr>
                                <th style={{ width: 36, textAlign: 'center' }}>Sel</th>
                                <th>SKU</th>
                                <th>Description</th>
                                <th>Dims (L×W×H in)</th>
                                <th className="num">Qty in Package</th>
                                <th className="num">Move Qty</th>
                                <th className="num">Unit Wgt (lbs)</th>
                                <th className="num">Total Wgt (lbs)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bItems.map(it => {
                                const checked = sel.has(it.id);
                                const maxQty = it.qty;
                                const row = it.row;
                                const unitLbs = rowWeightLbsRaw(row) || 0;
                                const moveQty = sel.get(it.id) || '';
                                const totalW = unitLbs && maxQty ? +(unitLbs * maxQty).toFixed(2) : '';
                                return (
                                  <tr key={`p-${b.id}-it-${it.id}`}>
                                    <td style={{ textAlign: 'center' }}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleBundleItem(b.id, it.id, maxQty)}
                                        disabled={purchasing}
                                      />
                                    </td>
                                    <td>{row?.sku}</td>
                                    <td>{row?.itemdescription}</td>
                                    <td>{rowDims(row)}</td>
                                    <td className="num">{maxQty}</td>
                                    <td className="num">
                                      <input
                                        className="elbl-input small"
                                        type="number"
                                        min={1}
                                        max={maxQty}
                                        value={checked ? moveQty : ''}
                                        onChange={(e) => setMoveQty(b.id, it.id, e.target.value, maxQty)}
                                        disabled={!checked || purchasing}
                                        placeholder="0"
                                      />
                                    </td>
                                    <td className="num">{unitLbs || '—'}</td>
                                    <td className="num">{totalW || '—'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : (
                          <div className="elbl-empty">No items in this package.</div>
                        )}
                      </div>
                      {/* Dimensions row */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginTop: 10 }}>
                        <label className="elbl-field small">
                          <span>Length (in){bItems.length > 0 ? ' *' : ''}</span>
                          <input
                            className="elbl-input" type="number" min="0" step="0.1"
                            value={b.length}
                            onChange={(e) => setBundleDim(b.id, 'length', e.target.value)}
                            disabled={purchasing}
                          />
                        </label>
                        <label className="elbl-field small">
                          <span>Width (in){bItems.length > 0 ? ' *' : ''}</span>
                          <input
                            className="elbl-input" type="number" min="0" step="0.1"
                            value={b.width}
                            onChange={(e) => setBundleDim(b.id, 'width', e.target.value)}
                            disabled={purchasing}
                          />
                        </label>
                        <label className="elbl-field small">
                          <span>Height (in){bItems.length > 0 ? ' *' : ''}</span>
                          <input
                            className="elbl-input" type="number" min="0" step="0.1"
                            value={b.height}
                            onChange={(e) => setBundleDim(b.id, 'height', e.target.value)}
                            disabled={purchasing}
                          />
                        </label>
                        <label className="elbl-field small">
                          <span>Dim Unit</span>
                          <select
                            className="elbl-input"
                            value={b.dimUnit || 'IN'}
                            onChange={(e) => setBundleDim(b.id, 'dimUnit', e.target.value)}
                            disabled={purchasing}
                          >
                            <option value="IN">IN</option>
                            <option value="CM">CM</option>
                          </select>
                        </label>
                        <div className="muted" style={{ alignSelf: 'end', fontSize: 12 }}>
                          {rec?.needCuFt != null && <>Needed: <b>{rec.needCuFt.toFixed(3)}</b> ft³</>}
                          {b.length && b.width && b.height && (
                            <> &nbsp;•&nbsp; Current: <b>{((Number(b.length) * Number(b.width) * Number(b.height)) / 1728).toFixed(3)}</b> ft³</>
                          )}
                        </div>
                      </div>
                      {/* Weight + Shipping Box */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, marginTop: 10, alignItems: 'end' }}>
                        <label className="elbl-field small">
                          <span>Weight (lbs){bItems.length > 0 ? ' *' : ''}</span>
                          <input
                            className="elbl-input" type="number" min="0" step="0.1"
                            value={b.weight}
                            onChange={(e) => setBundleWeight(b.id, e.target.value)}
                            disabled={purchasing}
                          />
                        </label>
                        <label className="elbl-field" style={{ minWidth: 260 }}>
                          <span>Shipping Box</span>
                          <select
                            className="elbl-input"
                            value={b.selectedShipPkgId || ''}
                            onChange={(e) => setBundleSelectedPkg(b.id, e.target.value)}
                            disabled={purchasing || shipPkgOptions.length === 0}
                          >
                            <option value="">— Select a box —</option>
                            {shipPkgOptions.map(pkg => (
                              <option key={pkg.id} value={pkg.id}>
                                {pkg.clientid ? '' : '[Global] '}
                                {pkg.name} ({pkg.code}) — {pkg.length}×{pkg.width}×{pkg.height} {pkg.dim_unit} • {pkg.volume_ft?.toFixed?.(3)} ft³
                              </option>
                            ))}
                          </select>
                        </label>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          {rec?.rec && (
                            <button
                              className="btn"
                              type="button"
                              onClick={() => setBundleSelectedPkg(b.id, String(rec.rec.id))}
                              disabled={purchasing}
                              title="Use recommended box"
                            >
                              Use recommended
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div style={{ marginTop: 8 }}>
                  <button
                    className="btn"
                    onClick={() =>
                      setBundlePackages(prev => [...prev, { id: (prev.at(-1)?.id || 0) + 1, weight: '', unit: 'LBS', length: '', width: '', height: '', dimUnit: 'IN', selectedShipPkgId: '', items: [] }])
                    }
                    disabled={purchasing}
                  >
                    <FaPlus /> Add Another Package
                  </button>
                </div>
              </fieldset>
              {/* NEW: Delivery Options (between Packaged Shipments and Shipment Summary) */}
              <fieldset className="elbl-card" style={{ marginTop: 10 }}>
                <legend>Delivery Options</legend>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label className="mini-check" title="Residential / Home Delivery">
                    <input
                      type="checkbox"
                      checked={homeDelivery}
                      onChange={(e) => setHomeDelivery(e.target.checked)}
                      disabled={!supportsHomeDelivery || purchasing}
                    />
                    <span>Home Delivery (Residential)</span>
                  </label>
                  <label className="mini-check" title="Signature Required on delivery">
                    <input
                      type="checkbox"
                      checked={signatureRequired}
                      onChange={(e) => setSignatureRequired(e.target.checked)}
                      disabled={!supportsSignature || purchasing}
                    />
                    <span>Signature Required</span>
                  </label>
                </div>
                {/* Disclaimer about accessorials not included in rating responses */}
                <div
                  className="muted"
                  style={{ marginTop: 8, fontSize: 12, lineHeight: 1.45, display: 'flex', gap: 8, alignItems: 'flex-start' }}
                >
                  <FaExclamationTriangle style={{ marginTop: 2 }} />
                  <span>
                    <strong>Heads up:</strong> For UPS and FedEx, <em>signature-required and certain accessorial surcharges
                      are not included in rating responses</em>. They are applied only when purchasing labels. Rates shown above
                    are base transport estimates and your final charge may increase if you enable <em>Signature Required</em>.
                  </span>
                </div>
              </fieldset>
              {/* Shipment summary */}
              <fieldset className="elbl-card">
                <legend>Shipment Summary</legend>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                  <div>Total packages</div>
                  <div style={{ textAlign: 'right', fontWeight: 700 }}>{packageList.length}</div>
                </div>
              </fieldset>
            </div>
            {/* RIGHT: Ship-From + Ship-To + Rates */}
            <div className="elbl-right">
              <fieldset className="elbl-card" style={{ marginBottom: 8 }}>
                <legend>Ship From</legend>
                <div className="elbl-shipto">
                  <div><b>{shipAddrs.shipper.name || 'Shipper'}</b></div>
                  <div>
                    {shipAddrs.shipper.address?.address1 || ''}
                    {shipAddrs.shipper.address?.address2 ? `, ${shipAddrs.shipper.address.address2}` : ''}
                  </div>
                  <div>
                    {shipAddrs.shipper.address?.city || ''}
                    {shipAddrs.shipper.address?.state ? `, ${shipAddrs.shipper.address.state}` : ''}
                    {shipAddrs.shipper.address?.postalCode ? ` ${shipAddrs.shipper.address.postalCode}` : ''}
                  </div>
                  <div>{shipAddrs.shipper.address?.countryCode || 'US'}</div>
                  {shipAddrs.shipper.phone && (
                    <div style={{ marginTop: 8 }}>Phone: {shipAddrs.shipper.phone}</div>
                  )}
                </div>
              </fieldset>
              <fieldset className="elbl-card" style={{ marginBottom: 8 }}>
                <legend>Ship To</legend>
                <div className="elbl-shipto">
                  <div><b>{shipAddrs.shipTo.name || 'Recipient'}</b></div>
                  <div>{shipAddrs.shipTo.address1}{shipAddrs.shipTo.address2 ? `, ${shipAddrs.shipTo.address2}` : ''}</div>
                  <div>{shipAddrs.shipTo.city}{shipAddrs.shipTo.state ? `, ${shipAddrs.shipTo.state}` : ''}{shipAddrs.shipTo.postalCode ? ` ${shipAddrs.shipTo.postalCode}` : ''}</div>
                  <div>{shipAddrs.shipTo.countryCode || 'US'}</div>
                  <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <label className="elbl-field small">
                      <span>Phone</span>
                      <input
                        className="elbl-input"
                        type="tel"
                        placeholder="Enter phone"
                        value={shipToPhoneOverride}
                        onChange={(e) => setShipToPhoneOverride(e.target.value)}
                        disabled={purchasing}
                      />
                    </label>
                    <label className="elbl-field small">
                      <span>Email</span>
                      <input
                        className="elbl-input"
                        type="email"
                        placeholder="Enter email"
                        value={shipToEmailOverride}
                        onChange={(e) => setShipToEmailOverride(e.target.value)}
                        disabled={purchasing}
                      />
                    </label>
                  </div>
                </div>
              </fieldset>
              <fieldset className="elbl-card">
                <legend>Delivery Options & Rates</legend>
                {ratesError ? (
                  <div className="elbl-error">{ratesError}</div>
                ) : fetchingRates ? (
                  <div className="elbl-empty">Fetching rates…</div>
                ) : rates.length ? (
                  <>
                    <div className="elbl-rates-wrap">
                      <table className="elbl-table">
                        <thead>
                          <tr>
                            <th>Service</th>
                            <th className="num">Per-Label</th>
                            <th className="num">Price (all {packageList.length} labels)</th>
                            <th className="num">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rates.map((r, i) => {
                            const vm = Number(selectedProvider?.vendormarkup || 0);   // decimal, e.g., 0.05
                            const jm = Number(selectedProvider?.juicymarkup  || 0);   // decimal, e.g., 0.10
                            const m3 = Number(selectedProvider?.threeplmarkup || 0);  // decimal 0..1
                            const baseTotal = Number(r.totalCharges || 0);
                            // Apply markups sequentially using decimal percentages directly.
                            const vendorAmt = baseTotal * vm;
                            const baseAfterVendor = baseTotal + vendorAmt;
                            const juicyAmt = baseAfterVendor * jm;
                            const baseAfterJuicy = baseAfterVendor + juicyAmt;
                            const threeplAmt = baseAfterJuicy * m3;
                            const total = baseAfterJuicy + threeplAmt;
                            try {
                              // Debug: inspect markups and totals used for this rate row
                              console.log('[EOrderLabelModal][rates]', {
                                providerId: selectedProvider?.id,
                                serviceCode: r.serviceCode,
                                baseTotal,
                                vendormarkup: vm,
                                juicymarkup: jm,
                                threeplmarkup: m3,
                                vendorAmt,
                                juicyAmt,
                                threeplAmt,
                                totalWithMarkups: total,
                              });
                            } catch {}
                            const labelCount = Math.max(1, packageList.length || 0);
                            const perLabel = total / labelCount;
                            const perCarrier = baseTotal / labelCount;
                            const perVendor = vendorAmt / labelCount;
                            const perJuicy = juicyAmt / labelCount;
                            const per3pl = threeplAmt / labelCount;
                            const tooltipLines = [
                              `Carrier charge: ${fmtCurrency(perCarrier, r.currency || 'USD')}`,
                              vm ? `Vendor markup: ${fmtCurrency(perVendor, r.currency || 'USD')}` : null,
                              jm ? `Juicy markup: ${fmtCurrency(perJuicy, r.currency || 'USD')}` : null,
                              m3 ? `3PL markup: ${fmtCurrency(per3pl, r.currency || 'USD')}` : null,
                            ].filter(Boolean);
                            const perLabelTooltip = tooltipLines.join('\n');
                            const prov = String(selectedProvider?.servicetype || '').toLowerCase();
                            const isUspsRate = prov.includes('usps') || r.serviceCode?.toLowerCase() === 'usps_priority_mail';
                            const candidate = r.packageType || r.packageCode || '';
                            const friendly = friendlyPackageType(candidate);
                            const showPackage = isUspsRate && friendly; // Show only for USPS Priority Mail with valid package type
                            const packageSummary = summarizePackagesForDisplay(packageList);
                            return (
                              <tr key={`rate-${i}`}>
                                <td>
                                  <ServiceCell
                                    providerServiceType={selectedProvider?.servicetype}
                                    serviceCode={r.serviceCode}
                                    serviceName={r.serviceName}
                                  />
                                  <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
                                    {showPackage ? (
                                      <span>Box: {friendly}</span>
                                    ) : packageList.length > 1 && packageSummary ? (
                                      <span>Box: {packageSummary}</span>
                                    ) : null}
                                    {(showPackage || (packageList.length > 1 && packageSummary)) && (r.zone != null || r.daysInTransit || r.arrivesOn) ? ' • ' : ''}
                                    {r.zone != null ? `Zone ${r.zone}` : ''}
                                    {r.zone != null && (r.daysInTransit || r.arrivesOn) ? ' • ' : ''}
                                    {r.daysInTransit ? `${r.daysInTransit} day${r.daysInTransit === 1 ? '' : 's'}` : ''}
                                    {r.daysInTransit && r.arrivesOn ? ' • ' : ''}
                                    {r.arrivesOn ? `ETA ${r.arrivesOn}` : ''}
                                  </div>
                                </td>
                                <td className="num" title={perLabelTooltip || undefined}>
                                  {fmtCurrency(perLabel, r.currency || 'USD')}
                                </td>
                                <td className="num">{fmtCurrency(total, r.currency || 'USD')}</td>
                                <td className="num">
                                  <button className="btn primary" onClick={() => printLabels(r)} disabled={purchasing}>
                                    Purchase & Print
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="elbl-summary" style={{ marginTop: 10 }}>
                      {(() => {
                        const vm = Number(selectedProvider?.vendormarkup || 0);
                        const jm = Number(selectedProvider?.juicymarkup  || 0);
                        const m3 = Number(selectedProvider?.threeplmarkup || 0);
                        const totals = rates.map(r => {
                          const base = Number(r.totalCharges || 0);
                          const vendorAmt = base * vm;
                          const baseAfterVendor = base + vendorAmt;
                          const juicyAmt = baseAfterVendor * jm;
                          const baseAfterJuicy = baseAfterVendor + juicyAmt;
                          const threeplAmt = baseAfterJuicy * m3;
                          return baseAfterJuicy + threeplAmt;
                        });
                        const perLabels = rates.map(r => {
                          const base = Number(r.totalCharges || 0);
                          const vendorAmt = base * vm;
                          const baseAfterVendor = base + vendorAmt;
                          const juicyAmt = baseAfterVendor * jm;
                          const baseAfterJuicy = baseAfterVendor + juicyAmt;
                          const threeplAmt = baseAfterJuicy * m3;
                          const total = baseAfterJuicy + threeplAmt;
                          return packageList.length ? total / packageList.length : total;
                        });
                        const minTotal = totals.length ? Math.min(...totals) : null;
                        const minPer = perLabels.length ? Math.min(...perLabels) : null;
                        return (
                          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 6, opacity: 0.95 }}>
                            <div><b>Labels:</b></div><div>{packageList.length}</div>
                            <div><b>Cheapest per-label:</b></div><div>{minPer != null ? fmtCurrency(minPer) : '—'}</div>
                            <div><b>Cheapest total:</b></div><div>{minTotal != null ? fmtCurrency(minTotal) : '—'}</div>
                          </div>
                        );
                      })()}
                    </div>
                  </>
                ) : (<div className="elbl-empty">No rates yet — choose provider and click Get Rates.</div>)}
              </fieldset>
            </div>
          </div>
        </div>
        <div className="elbl-foot">
          <button className="btn" onClick={onClose} disabled={purchasing}>Close</button>
          <button
            className="btn primary"
            onClick={requestRates}
            disabled={
              fetchingRates || !providerId || purchasing || loadingCfg ||
              ownMissingWeights.length > 0 || missingBundleWeights || missingBundleDims
            }
            title={
              ownMissingWeights.length > 0 ? 'Some own-container items are missing weight' :
              missingBundleWeights ? 'Enter weight for all user-assembled packages' :
              missingBundleDims ? 'Enter L×W×H for all user-assembled packages' : ''
            }
          >
            {fetchingRates ? 'Getting Rates…' : 'Get Rates'}
          </button>
        </div>
      </div>
    </div>
  );
}
